import React from "react";
import type { StaticImageData } from "next/image";
import {
  ContentWrapper,
  ContentImage,
  ContentTextWrapper,
  TextTitle,
  TextDesc,
  PointBadge,
  PointLabel,
  PointValue,
  Banner,
  BannerText,
  ButtonGroup,
  ResultContentContainer
} from "./practice-result.styles";
import { Button } from "design-system/components/button/button";
import { ButtonSize } from "design-system/components/button/button.types";
import { Icon } from "design-system/components/icon/icon";
import { ColearnIconsId } from "design-system/foundation/icon-font/colearn-icons";
import CorrectImage from "public/assets-v2/illustrations/correct_answer.svg";
import IncorrectImage from "public/assets-v2/illustrations/incorrect_answer.svg";
import IncorrectMcqImage from "public/assets-v2/illustrations/incorrect_answer_mcq.svg";
import NotEligibleImage from "public/assets-v2/illustrations/Image_blur.svg";
import NotRightImage from "public/assets-v2/illustrations/Nara_coco_detective.svg";

export type ResultVariant =
  | "notLegible"
  | "notRight"
  | "incorrect"
  | "incorrectMcq"
  | "correct"
  | "correctMcq";

interface PracticeResultProps {
  variant: ResultVariant;
  points?: number;
  isLastQuestion?: boolean;
  onClick: () => void;
  onClose?: () => void;
}

const VARIANT_CONTENT: Record<
  ResultVariant,
  {
    image: StaticImageData;
    title: string;
    desc?: string;
    showPoints: boolean;
    showBanner: boolean;
    primaryText: string | null;
    primaryIcon?: ColearnIconsId;
    showSecondary: boolean;
  }
> = {
  notLegible: {
    image: NotEligibleImage,
    title: "Fotomu kurang jelas, nih",
    desc: "Coba foto ulang ya, pastikan tulisanmu terlihat jelas",
    showPoints: false,
    showBanner: false,
    primaryText: "Foto ulang",
    primaryIcon: "general-camera",
    showSecondary: true
  },
  notRight: {
    image: NotRightImage,
    title: "Hmm, ini bukan foto jawaban",
    desc: "Coba foto ulang dengan jawaban yang benar ya",
    showPoints: false,
    showBanner: false,
    primaryText: "Foto ulang",
    primaryIcon: "general-camera",
    showSecondary: true
  },
  incorrect: {
    image: IncorrectImage,
    title: "Yah, jawabanmu belum tepat",
    desc: "Jangan nyerah! Cek pembahasan biar makin paham",
    showPoints: false,
    showBanner: false,
    primaryText: "Lihat pembahasan",
    showSecondary: false
  },
  incorrectMcq: {
    image: IncorrectMcqImage,
    title: "Yah, jawabanmu belum tepat",
    desc: "Nggak apa-apa, tarik napas dulu... Lanjut soal berikutnya lebih santai dan fokus",
    showPoints: false,
    showBanner: false,
    primaryText: null,
    showSecondary: false
  },
  correct: {
    image: CorrectImage,
    title: "Asiik! jawaban kamu benar 💪",
    desc: undefined,
    showPoints: true,
    showBanner: true,
    primaryText: "Lihat pembahasan",
    showSecondary: false
  },
  correctMcq: {
    image: CorrectImage,
    title: "Asiik! jawaban kamu benar 💪",
    desc: undefined,
    showPoints: true,
    showBanner: false,
    primaryText: null,
    showSecondary: false
  }
};

export const PracticeResult: React.FC<PracticeResultProps> = ({
  variant,
  points,
  isLastQuestion,
  onClick,
  onClose
}) => {
  const content = VARIANT_CONTENT[variant];

  const getPrimaryText = () => {
    if (content.primaryText) return content.primaryText;
    return isLastQuestion ? "Selesai" : "Soal berikutnya";
  };

  return (
    <ContentWrapper>
      <ResultContentContainer>
        {content.image && <ContentImage src={content.image} alt={variant} />}
        <ContentTextWrapper>
          <TextTitle>{content.title}</TextTitle>
          {content.desc && <TextDesc>{content.desc}</TextDesc>}
        </ContentTextWrapper>

        {content.showPoints && points !== undefined && (
          <PointBadge>
            <PointLabel>Berhasil dapat</PointLabel>
            <PointValue>{points} Poin!</PointValue>
          </PointBadge>
        )}
      </ResultContentContainer>

      <ButtonGroup>
        {content.showBanner && (
          <Banner>
            <BannerText>
              Cek pembahasan Kak Caca yuk! Siapa tau ada cara berbeda yang bikin
              kamu makin paham
            </BannerText>
          </Banner>
        )}
        <Button
          appearance="general"
          variant="primary"
          size={ButtonSize.lg}
          isFullWidth
          onClick={onClick}
        >
          {content.primaryIcon && <Icon name={content.primaryIcon} />}
          {getPrimaryText()}
        </Button>
        {content.showSecondary && onClose && (
          <Button
            appearance="general"
            variant="secondary"
            size={ButtonSize.lg}
            isFullWidth
            onClick={onClose}
          >
            Kembali
          </Button>
        )}
      </ButtonGroup>
    </ContentWrapper>
  );
};
